import { useEffect, useState, useCallback } from "react";
import { Check, X, Trash2, Clock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";

const FILTERS = ["all", "pending", "approved", "rejected"];
const BADGE = { approved: "bg-forest text-ivory", pending: "bg-champagne text-espresso", rejected: "bg-espresso/20 text-espresso" };

export default function AdminTestimonials() {
  const [items, setItems] = useState([]);
  const [filter, setFilter] = useState("all");

  const load = useCallback(() => api.get("/admin/testimonials").then((r) => setItems(r.data)).catch(() => {}), []);
  useEffect(() => { load(); }, [load]);

  const setStatus = async (id, status) => {
    await api.put(`/admin/testimonials/${id}`, { status });
    toast.success(status === "approved" ? "Approved — now live" : `Marked ${status}`);
    load();
  };
  const remove = async (id) => {
    if (!window.confirm("Delete this testimonial?")) return;
    await api.delete(`/admin/testimonials/${id}`);
    toast.success("Deleted");
    load();
  };

  const shown = filter === "all" ? items : items.filter((i) => i.status === filter);
  const pendingCount = items.filter((i) => i.status === "pending").length;

  return (
    <div data-testid="admin-testimonials">
      <h1 className="font-serif text-4xl text-espresso">Testimonials</h1>
      <p className="text-espresso/60 mt-2">{items.length} total · {pendingCount} awaiting approval</p>

      <div className="inline-flex bg-taupe rounded-full p-1.5 mt-6 flex-wrap">
        {FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`testimonials-filter-${f}`}
            className={`rounded-full px-5 py-2 text-sm font-medium capitalize transition-colors ${filter === f ? "bg-espresso text-ivory" : "text-espresso hover:text-champagne"}`}>
            {f}
          </button>
        ))}
      </div>

      <div className="mt-8 space-y-4">
        {shown.length === 0 && <p className="text-espresso/50">No testimonials in this view.</p>}
        {shown.map((t) => (
          <div key={t.id} className="bg-taupe rounded-2xl p-6" data-testid={`testimonial-row-${t.id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <span className={`inline-flex items-center gap-1 rounded-full px-3 py-1 text-xs ${BADGE[t.status] || ""}`}>
                  {t.status === "pending" && <Clock size={12} />} {t.status}
                </span>
                <p className="font-serif text-lg text-espresso mt-3">{t.name || "Anonymous"} <span className="text-espresso/50 text-sm font-sans">· {t.job_title}, {t.industry}</span></p>
                <p className="text-espresso/50 text-xs mt-1">{new Date(t.created_at).toLocaleString()}</p>
              </div>
              <div className="flex gap-2 shrink-0">
                {t.status !== "approved" && (
                  <button onClick={() => setStatus(t.id, "approved")} data-testid={`testimonial-approve-${t.id}`} className="inline-flex items-center gap-1 bg-forest text-ivory rounded-full px-4 py-2 text-sm hover:opacity-90 transition-opacity"><Check size={15} /> Approve</button>
                )}
                {t.status !== "rejected" && (
                  <button onClick={() => setStatus(t.id, "rejected")} data-testid={`testimonial-reject-${t.id}`} className="inline-flex items-center gap-1 bg-ivory text-espresso rounded-full px-4 py-2 text-sm hover:bg-champagne transition-colors"><X size={15} /> Reject</button>
                )}
                <button onClick={() => remove(t.id)} data-testid={`testimonial-delete-${t.id}`} className="p-2 rounded-full bg-ivory hover:bg-red-200 transition-colors"><Trash2 size={15} className="text-espresso" /></button>
              </div>
            </div>
            <blockquote className="font-serif text-xl text-espresso/90 mt-4 border-t border-champagne/30 pt-4">&ldquo;{t.quote}&rdquo;</blockquote>
          </div>
        ))}
      </div>
    </div>
  );
}
